export interface SupportedLanguage {
  code: string;
  label: string;
  flag: string;
}

export const AVAILABLE_LANGUAGES: SupportedLanguage[] = [
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

/**
 * Parses a localized settings JSON string (or legacy plain text) into a key-value map per language code.
 * E.g. {"pl": "...", "en": "..."} or raw legacy string -> { "pl": raw }
 */
export function parseLocalizedMap(jsonOrRaw: string | null | undefined): Record<string, string> {
  if (!jsonOrRaw || typeof jsonOrRaw !== 'string') {
    return {};
  }

  const trimmed = jsonOrRaw.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const result: Record<string, string> = {};
        for (const [key, val] of Object.entries(parsed)) {
          if (typeof val === 'string') {
            result[key] = val;
          }
        }
        return result;
      }
    } catch {
      // Not valid JSON, fallback to plain text treatment
    }
  }

  // If plain text, default to Polish
  return trimmed.length > 0 ? { pl: trimmed } : {};
}

/**
 * Serializes a language-to-content map into a clean JSON string for persistence.
 * Removes empty entries. If all entries are empty, returns null.
 */
export function serializeLocalizedMap(map: Record<string, string>): string | null {
  const cleaned: Record<string, string> = {};
  let hasAny = false;

  for (const [lang, val] of Object.entries(map)) {
    if (val && val.trim().length > 0) {
      cleaned[lang] = val.trim();
      hasAny = true;
    }
  }

  return hasAny ? JSON.stringify(cleaned) : null;
}

/**
 * Retrieves the text for a specific language with intelligent fallback.
 */
export function parseLocalizedText(
  jsonOrRaw: string | null | undefined,
  lang: string,
  fallbackLang = 'pl'
): string {
  const map = parseLocalizedMap(jsonOrRaw);
  const normalizedLang = lang.split('-')[0].toLowerCase();

  if (map[normalizedLang] && map[normalizedLang].trim().length > 0) {
    return map[normalizedLang];
  }

  if (map[fallbackLang] && map[fallbackLang].trim().length > 0) {
    return map[fallbackLang];
  }

  // Return first available language value if neither requested nor fallback found
  const values = Object.values(map);
  return values.length > 0 ? values[0] : '';
}
